import RoleBasedLayout from "./RoleBasedLayout";
import { useState, useMemo } from "react";
import { UsersRound, Boxes, X, Pencil, Eye, Trash2, MoveLeft, Download, CirclePlus } from "lucide-react";
import {
    useDepartments, useCreateDepartment, useDeleteDepartment,
    useUser, useGetPermissions, useDeleteEmployee
} from "../Use-auth";
import Button from "../ui/Button";
import CreateTeam from "../ui/CreateTeam";
import SkeletonPage from "../ui/SkeletonPage ";
import PermissionPopup from "../ui/PermissionPopup";

const InfoRow = ({ label, value, className }) => (
    <tr className="text-[10px] sm:text-[12px]">
        <td className="px-4 py-2 font-medium text-gray-600 w-1/3">
            {label}
        </td>

        <td className={`px-4 py-2 text-gray-800 ${className} inline-block `}>
            {value || "-"}
        </td>
    </tr >
);

const formatDateSafe = (date) => {
    if (!date) return "-";

    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";
    if (d.getFullYear() < 1900) return "-";

    return d.toLocaleDateString("en-IN");
};

export default function MyTeam() {
    const deleteDepartment = useDeleteDepartment();
    const { data: user } = useUser();
    const createDepartment = useCreateDepartment();
    const { data: existingPermissions } = useGetPermissions();
    const deleteEmployee = useDeleteEmployee();
    const [activeTab, setActiveTab] = useState("team");
    const [popup, setPopup] = useState(false);
    const [departmentName, setDepartmentName] = useState("");
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [showCreateTeam, setShowCreateTeam] = useState(false);
    const [expandedDeptId, setExpandedDeptId] = useState(null);
    const [editEmployee, setEditEmployee] = useState(null);
    const [department, setDepartment] = useState('');
    const [deptSearch, setDeptSearch] = useState("");
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState('Active');
    const [previewEmployee, setPreviewEmployee] = useState(null);
    const [loading, setLoading] = useState(false);

    const {
        data: departments = [],
        isLoading,
        refetch: refetchDepartments
    } = useDepartments();

    const deptSearchText = deptSearch.toLowerCase().trim();

    const filteredDepartments = departments.filter((dept) =>
        dept.departmentName?.toLowerCase().includes(deptSearchText)
    );

    const isAdmin = user?.department === "Admin";
    const currentUserPermissions = useMemo(() => {
        if (isAdmin) {
            return {
                hr: {
                    team_view: true,
                    add_employee: true,
                },
            };
        }

        return existingPermissions?.find(
            (p) => p.userId === user?.userId
        );
    }, [existingPermissions, user, isAdmin]);

    const canViewHome =
        isAdmin || currentUserPermissions?.hr?.team_view;

    const canAddEmployee =
        isAdmin || currentUserPermissions?.hr?.add_employee === true;

    const handleAddDepartment = async () => {
        if (!departmentName.trim()) return;
        setLoading(true);
        try {
            await createDepartment.mutateAsync({
                departmentName: departmentName.trim(),
                createdBy: {
                    userId: user.userId,
                    fullName: user.fullName || user.username,
                    role: user.role,
                }
            });
            setDepartmentName("");
            setPopup(false);
            refetchDepartments();
            setLoading(false);
        } catch (err) {
            console.error("Failed to create department:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDepartment = async (id) => {
        if (!window.confirm("Are you sure you want to delete this department?")) return;

        try {
            await deleteDepartment.mutateAsync(id);
        } catch (err) {
            const message =
                err.response?.data?.message || "Failed to delete department";
            alert(message);
        }
    };

    const handleExportAllEmployeesCSV = () => {
        if (!employees.length) return;

        const headers = [
            "Employee ID",
            "First Name",
            "Last Name",
            "Full Name",
            "Office Email",
            "Department",
            "Designation",
            "Role",
            "Status",
            "Source of Hire",
            "Joining Date",
            "Total Experience",
            "Annual Salary",
            "Resignation Date",
            "Shift",
            "Training Start",
            "Training End",
            "Training Period",
            "Bank Name",
            "Account Number",
            "IFSC",
            "DOB",
            "Age",
            "Gender",
            "Marital Status",
            "Mobile",
            "Personal Email",
            "Present Address",
            "Permanent Address",
            "Aadhaar",
            "PAN",
            "Guardian Name",
            "Guardian Contact",
            "Relationship",
            "Institutes",
            "Degrees",
            "Specializations",
            "Completion Dates",
            "Companies",
            "Titles",
            "From Dates",
            "To Dates",
            "Descriptions",
        ];

        const rows = employees.map(emp => {
            const work = emp.workInformation || {};
            const personal = emp.personalDetails || {};

            const institutes = emp.educationDetails?.map(e => e.institute).join(" | ") || "";
            const degrees = emp.educationDetails?.map(e => e.degree).join(" | ") || "";
            const specs = emp.educationDetails?.map(e => e.specialization).join(" | ") || "";
            const completions = emp.educationDetails?.map(e =>
                e.completionDate ? new Date(e.completionDate).toLocaleDateString() : ""
            ).join(" | ") || "";

            const companies = emp.workExperience?.map(w => w.company).join(" | ") || "";
            const titles = emp.workExperience?.map(w => w.title).join(" | ") || "";
            const froms = emp.workExperience?.map(w => w.from ? new Date(w.from).toLocaleDateString() : "").join(" | ") || "";
            const tos = emp.workExperience?.map(w => w.to ? new Date(w.to).toLocaleDateString() : "").join(" | ") || "";
            const descs = emp.workExperience?.map(w => w.description).join(" | ") || "";

            const formatValue = (val, isCurrency = false) => {
                if (val == null || val === "" || val === 0) return "-";
                return isCurrency ? `₹ ${Number(val).toLocaleString("en-IN")}` : val;
            };

            const lastPayroll = emp.payroll?.[emp.payroll.length - 1];

            return [
                emp.employeeId || "",
                work.firstName || "",
                work.lastName || "",
                `${work.firstName || ""} ${work.lastName || ""}`.trim(),
                emp.officeEmail || "",
                emp.departmentName || "",
                work.designation || "",
                emp.role || "",
                emp.employeeStatus || "",
                work.sourceOfHire || "",
                work.joiningDate ? new Date(work.joiningDate).toLocaleDateString() : "",
                work.totalExperience || "",
                lastPayroll ? formatValue(lastPayroll.annualCtc, true) : "-",
                work.resignationDate ? new Date(work.resignationDate).toLocaleDateString() : "",
                work.employeeShift || "",
                work.trainingStartDate ? new Date(work.trainingStartDate).toLocaleDateString() : "",
                work.trainingEndDate ? new Date(work.trainingEndDate).toLocaleDateString() : "",
                work.trainingPeriod || "",
                work.bankName || "",
                work.bankAccountNumber || "",
                work.iFSCcode || "",
                personal.dob ? new Date(personal.dob).toLocaleDateString() : "",
                personal.age || "",
                personal.gender || "",
                personal.maritalStatus || "",
                personal.mobile || "",
                personal.personalEmail || "",
                personal.address || "",
                personal.permanentLine || "",
                personal.aadhaar || "",
                personal.pan || "",
                personal.guardianName || "",
                personal.guardianContact || "",
                personal.relationship || "",
                institutes,
                degrees,
                specs,
                completions,
                companies,
                titles,
                froms,
                tos,
                descs,
            ];
        });

        const csvContent =
            "\uFEFF" +
            headers.join(",") +
            "\n" +
            rows.map(row =>
                row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")
            ).join("\n");

        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;"
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "all_employees_full_data.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    const employees = useMemo(() => {
        return departments.flatMap((dept) =>
            (dept.employees || []).map((emp) => ({
                ...emp,

                employeeId: emp.employee_id,
                officeEmail: emp.office_email,
                employeeStatus: emp.employee_status,
                role: emp.role,
                departmentName: dept.departmentName,

                workInformation: {
                    firstName: emp.first_name,
                    lastName: emp.last_name,
                    image: emp.image,
                    ...emp.workInformation
                },

                personalDetails: emp.personalDetails,
                workExperience: emp.workExperience,
                educationDetails: emp.educationDetails,

                createdAt: emp.created_at || null
            }))
        );
    }, [departments]);

    const filteredEmployees = useMemo(() => {
        const searchText = employeeSearch.toLowerCase().trim();
        const selectedDept = department.toLowerCase().trim();
        const selectedStatus = statusFilter.toLowerCase().trim();

        return employees
            .sort((a, b) => {
                const dateA = new Date(
                    a.workInformation?.createdAt ||
                    a.createdAt ||
                    a.updatedAt ||
                    0
                );

                const dateB = new Date(
                    b.workInformation?.createdAt ||
                    b.createdAt ||
                    b.updatedAt ||
                    0
                );

                return dateB - dateA;
            })

            .filter((emp) => {
                const fullName =
                    `${emp.workInformation?.firstName || ""} ${emp.workInformation?.lastName || ""}`.toLowerCase();

                const email = (emp.officeEmail || "").toLowerCase();
                const empDept = (emp.departmentName || "").toLowerCase();
                const role = (emp.role || "").toLowerCase();
                const id = (emp.employeeId || "").toLowerCase();
                const status = (emp.employeeStatus || "").toLowerCase();

                const dateText = (
                    emp.workInformation?.createdAt ||
                    emp.createdAt ||
                    emp.updatedAt ||
                    ""
                ).toLowerCase();

                const matchesSearch =
                    fullName.includes(searchText) ||
                    email.includes(searchText) ||
                    role.includes(searchText) ||
                    id.includes(searchText) ||
                    dateText.includes(searchText);

                const matchesDept = !selectedDept || empDept === selectedDept;
                const matchesStatus = !selectedStatus || status === selectedStatus;

                return matchesSearch && matchesDept && matchesStatus;
            });

    }, [employees, employeeSearch, department, statusFilter]);

    const handleDelete = async (employeeId) => {
        if (!window.confirm("Are you sure you want to delete this employee?")) return;
        try {
            await deleteEmployee.mutateAsync(employeeId);
        } catch (err) {
            const message =
                err.response?.data?.message || "Failed to delete employee";
            alert(message);
        }
    }

    const calculateExperience = (joiningDate) => {
        if (!joiningDate) return "-";

        const joining = new Date(joiningDate);
        const today = new Date();

        let years = today.getFullYear() - joining.getFullYear();
        let months = today.getMonth() - joining.getMonth();

        if (months < 0) {
            years--;
            months += 12;
        }

        if (years === 0) return `${months} Month${months !== 1 ? "s" : ""}`;
        if (months === 0) return `${years} Year${years !== 1 ? "s" : ""}`;
        return `${years} Year${years !== 1 ? "s" : ""} ${months} Month${months !== 1 ? "s" : ""}`;
    };

    if (!existingPermissions) {
        return (
            <RoleBasedLayout>
                <SkeletonPage />
            </RoleBasedLayout>
        );
    }

    if (!canViewHome) {
        return (
            <RoleBasedLayout>
                <div className="relative h-[90.7vh] overflow-hidden">
                    <PermissionPopup />
                </div>
            </RoleBasedLayout>
        );
    }

    return (
        <RoleBasedLayout>
            <div className="relative">
                <div className="p-2 rs:p-6">
                    {!expandedDeptId && !showCreateTeam && (
                        <div className="bg-white rounded-lg shadow-sm p-1 xs:p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="default"
                                    noHover
                                    onClick={() => setActiveTab("team")}
                                    active={activeTab === "team"}
                                    className="flex gap-2 items-center"

                                >
                                    <UsersRound size={16} className="hidden zy:block" />
                                    Team Members
                                </Button>

                                <Button
                                    variant="default"
                                    noHover
                                    onClick={() => setActiveTab("members")}
                                    active={activeTab === "members"}
                                    className="flex gap-2 items-center"
                                >
                                    <Boxes size={16} className="hidden zy:block" />
                                    Departments
                                </Button>
                            </div>
                        </div>)}
                    {activeTab === "members" && !expandedDeptId && !showCreateTeam && (
                        <div className="mt-4">
                            <div className="flex flex-col sm:flex-col md:flex-row justify-between md:items-end gap-4 bg-white p-1 sm:p-4 rounded-lg shadow">
                                <div className="mb-2 md:mb-0">
                                    <h2 className="text-[14px] sm:text-[16px] md:text-[20px] font-semibold text-gray-800 w-full md:w-auto">
                                        Team
                                    </h2>
                                </div>

                                <div className="flex flex-col zy:flex-row sm:items-center gap-3 w-full sm:w-auto">
                                    <div className="w-full sm:w-64">
                                        <input
                                            type="text"
                                            value={deptSearch}
                                            onChange={(e) => setDeptSearch(e.target.value)}
                                            placeholder="Search"
                                            className="input w-full"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={() => setPopup(true)}
                                        variant="default"
                                        className="w-full zx:w-[300px] sm:w-auto"
                                    >
                                        + Create Department
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {popup && (
                        <div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 sm:px-4"
                            onClick={() => setPopup(false)}
                        >
                            <div
                                className="
      bg-white rounded-lg shadow-lg animate-scaleIn

      w-full
      max-w-[95%]
      sm:max-w-md
      relative
      p-3 sm:p-6
    "
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800">Create Department</h3>
                                    <button
                                        onClick={() => setPopup(false)}
                                        className="text-gray-500 hover:text-gray-800 text-xl cursor-pointer absolute right-[6px] top-[6px]"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>

                                <div className="mb-4">
                                    <label className="text-sm font-medium text-gray-600 mb-1 block">Department Name</label>
                                    <input
                                        type="text"
                                        value={departmentName}
                                        onChange={(e) => setDepartmentName(e.target.value)}
                                        placeholder="Search department..."
                                        className="input"
                                    />
                                </div>

                                <div className="flex justify-end gap-3">
                                    <Button
                                        variant="danger"
                                        onClick={() => setPopup(false)}
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        variant="success"
                                        onClick={handleAddDepartment}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <span className="loader-circle"></span>
                                        ) : (
                                            "Add"
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "members" && !expandedDeptId && !showCreateTeam && (
                        <div className="mt-6 bg-white p-1 sm:p-4 rounded-lg shadow">
                            <h3 className="text-[14px] sm:text-[16px] font-semibold text-gray-700 mb-4 lg:text-[14px]">
                                Departments
                            </h3>

                            <div className="overflow-x-auto">
                                <div className="grid grid-cols-3 zx:grid-cols-4 custom:grid-cols-5  gap-4 px-2 py-2 sm:px-4 sm:py-3 bg-gray-100 rounded-md text-xs font-semibold text-gray-600 lg:text-[14px]">
                                    <div className="col-span-1">Department Name</div>
                                    <div className="col-span-1 hidden custom:block">Added By</div>
                                    <div className="col-span-1">Employees</div>
                                    <div className="col-span-1 hidden zx:block">Added Time</div>
                                    <div className="col-span-1 text-right">Actions</div>
                                </div>

                                {isLoading ? (
                                    <SkeletonPage />
                                ) : filteredDepartments.length > 0 ? (
                                    filteredDepartments.map((dept) => (
                                        <div
                                            key={dept._id}
                                            className="grid grid-cols-3 zx:grid-cols-4 custom:grid-cols-5  gap-4 px-2 py-2 sm:px-4 sm:py-3 rounded-lg text-[12px] sm:text-[14px] border-l-4 border-blue-500 shadow-sm hover:bg-gray-50 items-center"
                                        >
                                            <div className="font-semibold text-gray-800">{dept.departmentName}</div>
                                            <div className="hidden custom:block text-gray-700 text-[12px] sm:text-[14px]">{dept.createdBy?.userId} - {dept.createdBy?.fullName || dept.createdBy?.username || "-"}</div>
                                            <div className="text-gray-700 font-medium text-[12px] sm:text-[14px]">{dept.employees?.length || 0}</div>
                                            <div className="hidden zx:block text-gray-700 text-[12px] sm:text-[14px]">{dept.createdAt ? new Date(dept.createdAt).toLocaleString() : "-"}</div>
                                            <div className="flex justify-end text-[12px] sm:text-[14px]">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteDepartment(dept._id);
                                                    }}
                                                    className="text-red-600 p-1 rounded hover:text-red-800 cursor-pointer hover:bg-red-100"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center text-gray-400 py-6 lg:text-[14px]">
                                        No departments yet.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === "team" && !showCreateTeam && (<>
                        <div className="mt-4 bg-gray-50 p-0 sm:p-4 rounded-lg space-y-2">
                            <div className="flex flex-col  bg-white p-1 sm:p-4 rounded-lg shadow gap-4 md:flex-row md:justify-between md:items-end flex-wrap">
                                {/* Title */}
                                <h2 className="text-[14px] sm:text-[16px] md:text-[20px] font-semibold text-gray-800 w-full md:w-auto">
                                    All Employees
                                </h2>

                                {/* Filters & Actions */}
                                <div className="flex flex-col md:flex-row md:items-center md:gap-4 w-full flex-wrap gap-2">
                                    {/* Search Input */}
                                    <input
                                        type="text"
                                        value={employeeSearch}
                                        onChange={(e) => setEmployeeSearch(e.target.value)}
                                        placeholder="Search employees..."
                                        className="input text-[12px] sm:text-sm flex-1 min-w-[120px] max-w-full sm:max-w-[250px]"
                                    />

                                    {/* Filters */}
                                    <div className="flex flex-row sm:gap-3 mt-2 md:mt-0 w-full sm:w-auto flex-wrap gap-2">
                                        <select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            className="input text-sm max-[540px]:text-xs flex-1 min-w-[120px] sm:min-w-[150px] sm:max-w-[200px] px-3 py-2 max-[540px]:px-2 max-[540px]:py-2"
                                        >
                                            <option value="">All Status</option>
                                            <option value="Active">Active</option>
                                            <option value="Trainee">Trainee</option>
                                            <option value="Resigned">Resigned</option>
                                            <option value="Terminated">Terminated</option>
                                        </select>

                                        <select
                                            value={department}
                                            onChange={(e) => setDepartment(e.target.value)}
                                            className="input text-sm max-[540px]:text-xs flex-1 min-w-[120px] sm:min-w-[150px] sm:max-w-[200px] px-3 py-2 max-[540px]:px-2 max-[540px]:py-2"
                                        >
                                            <option value="">All Departments</option>
                                            <option value="Admin">Admin</option>
                                            <option value="HR">HR</option>
                                            <option value="Management">Management</option>
                                            <option value="Sales">Sales</option>
                                            <option value="Development">Development</option>
                                            <option value="Design">Design</option>
                                            <option value="Facilities">Facilities</option>
                                        </select>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-row gap-2 mt-2 md:mt-0 md:ml-auto flex-wrap">
                                        {canAddEmployee && (
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedDepartment(null);
                                                    setShowCreateTeam(true);
                                                }}
                                            >

                                                <span className="">+ Add Employee</span>
                                            </Button>
                                        )}

                                        <Button
                                            type="button"
                                            variant="success"
                                            onClick={handleExportAllEmployeesCSV}
                                        >
                                            <Download className="w-5 h-5 sm:hidden" />
                                            <span className="hidden sm:inline">Export CSV</span>
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full border rounded-lg overflow-hidden">

                                {/* HEADER */}
                                <div className="
grid
grid-cols-[1.5fr_120px_70px]
xs:grid-cols-[110px_1.3fr_2fr_120px_70px]
custom:grid-cols-[120px_1.3fr_2fr_1.3fr_120px_70px]
lg:grid-cols-[140px_80px_1.3fr_2fr_1.3fr_1.3fr_120px_70px]
bg-gray-100 text-xs font-semibold text-gray-600 px-2 py-2 sm:px-4 sm:py-3 text-[12px] sm:text-[14px]
">

                                    <div className="hidden xs:block text-[12px] sm:text-[14px]">Employee ID</div>
                                    <div className="hidden lg:block text-[12px] sm:text-[14px]">Photo</div>
                                    <div>Full Name</div>
                                    <div className="hidden xs:block text-[12px] sm:text-[14px]">Email</div>
                                    <div className="hidden custom:block text-[12px] sm:text-[14px]">Department</div>
                                    <div className="hidden lg:block text-[12px] sm:text-[14px]">Designation</div>
                                    <div className="text-[12px] sm:text-[14px]">Status</div>
                                    <div className="text-[12px] sm:text-[14px]">Actions</div>

                                </div>

                                <div className="w-full">
                                    {isLoading ? (
                                        <div className="py-6">
                                            <SkeletonPage />
                                        </div>
                                    ) : filteredEmployees.length > 0 ? (
                                        [...filteredEmployees].map((emp, index) => {
                                            const workInfo = emp.workInformation || {};
                                            const isAdminUser = user?.department === "Admin";
                                            const isHRUser = user?.department === "HR";
                                            const isEmpAdmin = emp.departmentName === "Admin";

                                            return (
                                                <div
                                                    key={emp._id || emp.employeeId || index}
                                                    className="
grid
grid-cols-[1.5fr_120px_70px]
xs:grid-cols-[110px_1.3fr_2fr_120px_70px]
custom:grid-cols-[120px_1.3fr_2fr_1.3fr_120px_70px]
lg:grid-cols-[140px_80px_1.3fr_2fr_1.3fr_1.3fr_120px_70px]
items-center text-sm border-b bg-white hover:bg-gray-100 
transition-colors duration-200 px-2 py-2 sm:px-4 sm:py-3 text-[12px] sm:text-[14px]
"
                                                >
                                                    <div className="truncate hidden xs:block ">
                                                        {emp.employeeId || "-"}
                                                    </div>
                                                    <div className="hidden lg:flex items-center">
                                                        {workInfo.image ? (
                                                            <img
                                                                src={workInfo.image}
                                                                className="w-8 h-8 rounded-full object-cover"
                                                                alt="profile"
                                                            />
                                                        ) : (
                                                            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-xs font-semibold">
                                                                {`${workInfo.firstName?.[0] || ""}${workInfo.lastName?.[0] || ""}`}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="truncate text-[12px] sm:text-[14px]">
                                                        {workInfo.firstName} {workInfo.lastName}
                                                    </div>

                                                    <div className="truncate hidden xs:block text-[12px] sm:text-[14px]" title={emp.officeEmail}>
                                                        {emp.officeEmail || "-"}
                                                    </div>

                                                    <div className="truncate hidden custom:block text-[12px] sm:text-[14px]">
                                                        {emp.departmentName || "-"}
                                                    </div>

                                                    <div className="truncate hidden lg:block text-[12px] sm:text-[14px]">
                                                        {emp.role || "-"}
                                                    </div>

                                                    <div>
                                                        <span
                                                            className={`px-2 py-1 text-[12px] sm:text-[14px] rounded-full text-xs font-medium inline-flex items-center
                                                                ${emp.employeeStatus === "Active"
                                                                    ? "bg-green-100 text-green-700"
                                                                    : emp.employeeStatus === "Terminated"
                                                                        ? "bg-red-100 text-red-700"
                                                                        : emp.employeeStatus === "Resigned"
                                                                            ? "bg-orange-100 text-orange-700"
                                                                            : emp.employeeStatus === "Trainee"
                                                                                ? "bg-blue-100 text-blue-700"
                                                                                : "bg-gray-100 text-gray-600"
                                                                }
  `}
                                                        >
                                                            {emp.employeeStatus || "-"}
                                                        </span>

                                                    </div>

                                                    <div className="flex gap-1 items-center text-[12px] sm:text-[14px]">
                                                        <Eye
                                                            size={18}
                                                            color="orange"
                                                            className="p-[2px] rounded cursor-pointer hover:bg-orange-100"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setPreviewEmployee(emp);
                                                            }}
                                                        />

                                                        {((isAdminUser) || (isHRUser && !isEmpAdmin)) && (
                                                            <>
                                                                <Pencil
                                                                    size={18}
                                                                    color="blue"
                                                                    className="p-[2px] rounded cursor-pointer hover:bg-blue-100"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setEditEmployee(emp);
                                                                        setSelectedDepartment(emp.departmentName);
                                                                        setShowCreateTeam(true);
                                                                        setExpandedDeptId(null);
                                                                        setActiveTab(null);
                                                                    }}
                                                                />

                                                                <Trash2
                                                                    size={18}
                                                                    color="red"
                                                                    className="p-[2px] rounded cursor-pointer hover:bg-red-100"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(emp.employeeId)
                                                                    }}
                                                                />
                                                            </>)}
                                                    </div>

                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center py-6 text-sm text-gray-400">
                                            No employees in this department
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </>)}

                    {showCreateTeam && (
                        <div>
                            <button
                                onClick={() => {
                                    setShowCreateTeam(false);
                                    setEditEmployee(null);
                                    setActiveTab("team");
                                }}
                                className="back-btn"
                            >
                                <MoveLeft size={16} />
                                <span>{selectedDepartment || "Back"}</span>
                            </button>

                            <CreateTeam
                                departments={departments}
                                defaultDepartment={selectedDepartment}
                                mode={editEmployee ? "edit" : "create"}
                                initialData={editEmployee}
                                onClose={() => {
                                    setShowCreateTeam(false);
                                    setEditEmployee(null);
                                    setActiveTab('team');
                                }}
                            />
                        </div>
                    )}
                    {previewEmployee && (() => {
                        const emp = previewEmployee;
                        const statusClass =
                            emp.employeeStatus === "Active"
                                ? "bg-green-100 text-green-700"
                                : emp.employeeStatus === "Terminated"
                                    ? "bg-red-100 text-red-700"
                                    : emp.employeeStatus === "Resigned"
                                        ? "bg-orange-100 text-orange-700"
                                        : emp.employeeStatus === "Trainee"
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-gray-100 text-gray-600";

                        console.log(emp)

                        return (
                            <div
                                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 sm:px-4"
                                onClick={() => setPreviewEmployee(null)}
                            >
                                <div
                                    className="
      bg-white relative rounded-lg
      w-full
      max-w-[95%]
      sm:max-w-[700px]
      lg:max-w-[1200px]
      xl:max-w-[1500px]

      h-[90vh]
      animate-scaleIn
      shadow-lg
      overflow-hidden
    "
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Header */}
                                    <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 px-4 py-2">
                                        <h2 className=" text-[16px] sm:text-[20px] text-white font-semibold">Employee Details</h2>

                                        <button
                                            onClick={() => setPreviewEmployee(null)}
                                            className="text-white hover:text-red-400"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>

                                    <div className="px-2 sm:px-6 pt-2 sm:pt-6 pb-16 sm:pb-20 overflow-y-auto h-full">
                                        <h2 className="text-[16px] sm:text-[20px] font-semibold mb-3">Work Information</h2>

                                        <table className="w-full border border-gray-200 rounded-md mb-3 sm:mb-6">
                                            <tbody className="divide-y">
                                                <InfoRow label="User ID" value={emp.employeeId} />

                                                <tr>
                                                    <td className="px-4 py-2 font-medium text-gray-600 text-[10px] sm:text-[12px]">
                                                        Employee Image
                                                    </td>

                                                    <td className="px-4 py-2">
                                                        {emp.workInformation?.image ? (
                                                            <img
                                                                src={emp.workInformation.image}
                                                                className="w-10 h-10 rounded-full object-cover"
                                                                alt="Employee"
                                                            />
                                                        ) : "-"}
                                                    </td>
                                                </tr>

                                                <InfoRow
                                                    label="First Name"
                                                    value={emp.workInformation?.firstName}
                                                />

                                                <InfoRow
                                                    label="Last Name"
                                                    value={emp.workInformation?.lastName}
                                                />

                                                <InfoRow
                                                    label="Office Email"
                                                    value={emp.officeEmail}
                                                />

                                                <InfoRow
                                                    label="Department"
                                                    value={emp.departmentName}
                                                />

                                                <InfoRow
                                                    label="Designation"
                                                    value={emp.role}
                                                />

                                                <InfoRow
                                                    label="Employee Status"
                                                    value={emp.employeeStatus}
                                                    className={`ml-[15px] ${statusClass}`}
                                                />

                                                <InfoRow
                                                    label="Source of Hire"
                                                    value={emp.workInformation?.sourceOfHire}
                                                />
                                                <InfoRow label="Date of Joining" value={formatDateSafe(emp.workInformation?.joiningDate)} />

                                                <InfoRow
                                                    label="Total Experience"
                                                    value={calculateExperience(emp.workInformation?.joiningDate)}
                                                />

                                                {/* <InfoRow label="Joining Salary" value={emp.workInformation?.joiningSalary || "-"} /> */}

                                                <InfoRow
                                                    label="Annual CTC"
                                                    value={(() => {
                                                        const latest = emp.payroll?.length
                                                            ? [...emp.payroll].sort(
                                                                (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
                                                            )[0]
                                                            : null;

                                                        return latest?.annualCtc
                                                            ? `₹ ${Number(latest.annualCtc).toLocaleString()}`
                                                            : "-";
                                                    })()}
                                                />
                                                <InfoRow label="Resignation Date" value={formatDateSafe(emp.workInformation?.resignationDate)} />

                                                <InfoRow label="Employee Shift" value={emp.workInformation?.employeeShift || "-"} />
                                                <InfoRow label="Training Start Date" value={formatDateSafe(emp.workInformation?.trainingStartDate)} />

                                                <InfoRow label="Training End Date" value={formatDateSafe(emp.workInformation?.trainingEndDate)} />

                                                <InfoRow label="Termination Date" value={formatDateSafe(emp.workInformation?.terminationDate)} />
                                                <InfoRow label="Training Period" value={emp.workInformation?.trainingPeriod || "-"} />
                                            </tbody>
                                        </table>

                                        {/* Bank Details */}
                                        <h2 className="text-[16px] sm:text-[20px] font-semibold mt-3 sm:mt-6 mb-3 ">
                                            Bank Details
                                        </h2>
                                        <table className="w-full border border-gray-200 rounded-md mb-3 sm:mb-6">
                                            <tbody className="divide-y">
                                                <InfoRow label="Bank Name" value={emp.workInformation?.bankName || "-"} />
                                                <InfoRow label="Bank Account Number" value={emp.workInformation?.bankAccountNumber || "-"} />
                                                <InfoRow label="IFSC Code" value={emp.workInformation?.iFSCcode || "-"} />
                                            </tbody>
                                        </table>

                                        {/* Personal Details */}
                                        <h2 className="text-[16px] sm:text-[20px] font-semibold mt-3 sm:mt-6 mb-3 ">
                                            Personal Details
                                        </h2>

                                        <table className="w-full border border-gray-200 rounded-md mb-3 sm:mb-6">
                                            <tbody className="divide-y">

                                                <InfoRow
                                                    label="Date of Birth"
                                                    value={formatDateSafe(emp.personalDetails?.dob) || "-"}
                                                />

                                                <InfoRow label="Age" value={emp.personalDetails?.age} />

                                                <InfoRow label="Gender" value={emp.personalDetails?.gender} />

                                                <InfoRow label="Marital Status" value={emp.personalDetails?.maritalStatus} />

                                                <InfoRow label="Mobile Number" value={emp.personalDetails?.mobile} />

                                                {/* <InfoRow label="Guardian Mobile Number" value={emp.personalDetails?.guardianmobile} /> */}

                                                <InfoRow label="Personal Email" value={emp.personalDetails?.personalEmail} />

                                                <InfoRow label="Present Address" value={emp.personalDetails?.address} />

                                                <InfoRow label="Permanent Address" value={emp.personalDetails?.permanentLine} />

                                                <InfoRow label="Aadhaar" value={emp.personalDetails?.aadhaar} />

                                                <InfoRow label="PAN" value={emp.personalDetails?.pan} />

                                                <InfoRow label="Guardian Name" value={emp.personalDetails?.guardianName} />

                                                <InfoRow label="Relationship" value={emp.personalDetails?.relationship} />

                                                <InfoRow label="Guardian Contact" value={emp.personalDetails?.guardianContact} />

                                            </tbody>
                                        </table>


                                        {/* Work Experience */}
                                        <h2 className="text-[16px] sm:text-[20px] font-semibold mt-3 sm:mt-6 mb-3">
                                            Work Experience
                                        </h2>

                                        <table className="w-full border border-gray-200 rounded-md mb-3 sm:mb-6">

                                            <thead className="bg-gray-100 text-[12px] sm:text-sm">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px]">Company</th>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px]">Title</th>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px] hidden rs:table-cell">From</th>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px] hidden rs:table-cell">To</th>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px] hidden rs:table-cell">Description</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {emp.workExperience?.length ? (
                                                    emp.workExperience.map((exp, i) => (
                                                        <tr key={i} className="border-t text-[12px] sm:text-sm">

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px]">{exp.company || "-"}</td>

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px]">{exp.title || "-"}</td>

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px] hidden rs:table-cell">
                                                                {formatDateSafe(exp.from) || "-"}
                                                            </td>

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px] hidden rs:table-cell">
                                                                {formatDateSafe(exp.to) || "-"}

                                                            </td>

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px] hidden rs:table-cell">
                                                                {exp.description || "-"}
                                                            </td>

                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-4 text-gray-400">
                                                            No work experience
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>


                                        {/* Education */}
                                        <h2 className="text-[16px] sm:text-[20px] font-semibold mt-3 sm:mt-6 mb-3">
                                            Education Details
                                        </h2>

                                        <table className="w-full border border-gray-200 rounded-md">

                                            <thead className="bg-gray-100 text-[12px] sm:text-sm" >
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px]">Institute</th>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px]">Degree</th>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px] hidden rs:table-cell">Specialization</th>
                                                    <th className="px-4 py-2 text-left text-[10px] sm:text-[12px] hidden rs:table-cell">Completion</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {emp.educationDetails?.length ? (
                                                    emp.educationDetails.map((edu, i) => (
                                                        <tr key={i} className="border-t text-[12px] sm:text-sm">

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px]">{edu.institute || "-"}</td>

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px]" >{edu.degree || "-"}</td>

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px] hidden rs:table-cell">{edu.specialization || "-"}</td>

                                                            <td className="px-4 py-2 text-[10px] sm:text-[12px] hidden rs:table-cell">
                                                                {formatDateSafe(edu.completionDate) || "-"}
                                                            </td>

                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center py-4 text-gray-400">
                                                            No education records
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                </div>
            </div>
        </RoleBasedLayout>
    )
}